import { ResumeData, Template, UserOverrides, DEFAULT_TEMPLATES } from './resumeTypes';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { generateResumeHTML } from './pdfFallback';
import { getResumeStyles } from './pdfStyles';
import { applyOverrides } from './resumeUtils';

/** Старый дефолт бэкенда — не из палитры шаблонов; не используем его. */
const BACKEND_LEGACY_ACCENT = '#1e40af';

/** Legacy/старые id шаблонов -> текущие id шаблонов рендера. */
const TEMPLATE_ID_ALIASES: Record<string, string> = {
  'modern-sidebar-dark': 'template-03',
  'modern-sidebar-light': 'template-03',
  'monochrome-minimal': 'template-02',
  'classic': 'template-01',
  'minimal': 'template-02',
  'creative': 'template-01',
  'professional': 'template-01',
  'elegant': 'template-01',
};

function resolveTemplateId(rawId: string | undefined): string {
  if (!rawId) return 'template-01';
  return TEMPLATE_ID_ALIASES[rawId] || rawId;
}

/**
 * Единая нормализация резюме перед выгрузкой в PDF (Dashboard, отклики, и т.д.).
 * Приводит данные с бэкенда к виду, ожидаемому шаблонами: title→position, template (строка→объект), цвета, lang.
 * Используйте перед getTemplateAndOverridesForPdf везде, где источник — API (getResume, getApplicationResumeData).
 */
export function normalizeResumeForPdf(resume: ResumeData): ResumeData {
  const r = { ...resume };
  const raw = r as unknown as Record<string, unknown>;
  if ((!r.position || String(r.position).trim() === '') && typeof raw.title === 'string' && (raw.title as string).trim() !== '') {
    r.position = raw.title as string;
  }
  const scheme = raw.colorScheme ?? raw.color_scheme;
  const templateIdRaw = typeof r.template === 'string'
    ? r.template
    : (r.template as { id?: string } | undefined)?.id || 'template-01';
  const templateId = resolveTemplateId(templateIdRaw);
  const existingColors = (r.template && typeof r.template === 'object' && (r.template as { colors?: unknown }).colors) as Record<string, string> | undefined;
  const colors = existingColors || (scheme && typeof scheme === 'object' ? (scheme as Record<string, string>) : undefined);
  r.template = { id: templateId, accent: (scheme as { primary?: string })?.primary, withPhoto: !!(r.photo || r.photoUrl), ...(colors && { colors }) } as unknown as ResumeData['template'];
  if (r.lang !== 'ru' && r.lang !== 'en') r.lang = 'ru';
  return r;
}

/**
 * Возвращает template и overrides для генерации PDF по данным резюме
 * (список резюме, отклики). Цвет берётся из template.colors, colorScheme или color_scheme.
 */
export function getTemplateAndOverridesForPdf(resume: ResumeData): { template: Template; overrides: UserOverrides } {
  const normalizedResume = normalizeResumeForPdf(resume);
  const templateId = (normalizedResume.template as { id?: string })?.id || (typeof normalizedResume.template === 'string' ? normalizedResume.template : 'template-01');
  const template = DEFAULT_TEMPLATES.find(t => t.id === templateId) || DEFAULT_TEMPLATES[0];
  const raw = normalizedResume as unknown as Record<string, unknown>;
  let colors =
    (normalizedResume.template as { colors?: UserOverrides['colors'] })?.colors ||
    (raw.colorScheme as UserOverrides['colors'] | undefined) ||
    (raw.color_scheme as UserOverrides['colors'] | undefined);
  const accentOnly = (normalizedResume.template as { accent?: string })?.accent;
  if (!colors && accentOnly && accentOnly !== BACKEND_LEGACY_ACCENT) {
    colors = { accent: accentOnly, primary: accentOnly, secondary: template.colors?.secondary };
  }
  if (colors && (colors.accent === BACKEND_LEGACY_ACCENT || colors.primary === BACKEND_LEGACY_ACCENT)) {
    colors = { ...colors, accent: template.colors?.accent ?? colors.accent, primary: template.colors?.primary ?? colors.primary };
  }
  const overrides: UserOverrides = colors ? { colors } : {};
  return { template, overrides };
}

/**
 * Строит PDF из DOM предпросмотра (.a4-preview-container .a4-sheet).
 * Если переданы template и overrides (например из откликов), фон сайдбара template-03
 * принудительно задаётся как акцент с 40% прозрачностью.
 * Возвращает экземпляр jsPDF.
 */
async function buildPDFFromPreview(
  resumeData: ResumeData,
  template?: Template,
  overrides?: UserOverrides
): Promise<jsPDF> {
  const exportContainer = document.querySelector('.a4-preview-container[data-pdf-export="true"]') as HTMLElement | null;
  if (!exportContainer) {
    throw new Error('PDF export container not found in DOM');
  }
  const a4Sheet = exportContainer.querySelector('.a4-sheet') as HTMLElement | null;
  if (!a4Sheet) {
    throw new Error('PDF export sheet not found in DOM');
  }

  const pageWidthMM = 210;
  const pageHeightMM = 297;
  const SCALE = 2;

  const A4_WIDTH_PX = Math.round(pageWidthMM * 3.779527559);
  const A4_HEIGHT_PX = Math.round(pageHeightMM * 3.779527559);

  const clone = a4Sheet.cloneNode(true) as HTMLElement;

  // Важно: НЕ ставить visibility:hidden на родителе — html2canvas рисует пустой канвас (в PDF белая страница).
  // Уводим за экран, оставляя элемент «видимым» для движка отрисовки.
  const wrapper = document.createElement('div');
  wrapper.setAttribute('aria-hidden', 'true');
  wrapper.style.position = 'fixed';
  wrapper.style.left = '-100000px';
  wrapper.style.top = '0';
  wrapper.style.width = `${A4_WIDTH_PX}px`;
  wrapper.style.overflow = 'visible';
  wrapper.style.zIndex = '0';
  wrapper.style.background = 'white';
  wrapper.style.pointerEvents = 'none';

  // min-height не A4: иначе scrollHeight всегда ≥ A4 и нельзя отличить «мало контента» от длинного резюме.
  clone.style.cssText = `
    width: ${A4_WIDTH_PX}px !important;
    max-width: ${A4_WIDTH_PX}px !important;
    box-sizing: border-box !important;
    height: auto !important;
    min-height: 0 !important;
    max-height: none !important;
    transform: none !important;
    margin: 0 !important;
    box-shadow: none !important;
    overflow: visible !important;
  `;

  const sheetContent = clone.querySelector('.a4-sheet-content') as HTMLElement | null;
  [clone, sheetContent].forEach((el) => {
    if (!el) return;
    el.style.setProperty('overflow', 'visible', 'important');
    el.style.setProperty('height', 'auto', 'important');
    el.style.setProperty('max-height', 'none', 'important');
    el.style.setProperty('min-height', '0', 'important');
  });

  const templateHeaders = clone.querySelectorAll('.template-01-header');
  templateHeaders.forEach((el) => {
    const htmlEl = el as HTMLElement;
    htmlEl.style.color = '#FFFFFF';
    htmlEl.style.removeProperty('-webkit-text-stroke');
    (htmlEl.style as any).webkitTextStroke = '';
    htmlEl.style.removeProperty('paint-order');
    htmlEl.style.textShadow = 'none';
  });

  clone.querySelectorAll('.t03-section-title-outline').forEach((el) => {
    const h = el as HTMLElement;
    h.style.setProperty('padding', '0 12px 12px 12px', 'important');
    h.style.setProperty('line-height', '1.2', 'important');
    h.style.setProperty('display', 'flex', 'important');
    h.style.setProperty('align-items', 'center', 'important');
  });

  clone.querySelectorAll('.t03-skill-bar').forEach((el) => {
    const bar = el as HTMLElement;
    bar.style.setProperty('margin-top', '6px', 'important');
  });

  const allElements = clone.querySelectorAll('*');
  allElements.forEach((el) => {
    const htmlEl = el as HTMLElement;
    const stroke = htmlEl.style.getPropertyValue('-webkit-text-stroke') ||
                   (htmlEl.style as any).webkitTextStroke;
    if (stroke) {
      htmlEl.style.color = '#FFFFFF';
      htmlEl.style.removeProperty('-webkit-text-stroke');
      (htmlEl.style as any).webkitTextStroke = '';
      htmlEl.style.removeProperty('paint-order');
    }
    // Для PDF-снимка стараемся не клиповать контент внутренними блоками.
    const cs = window.getComputedStyle(htmlEl);
    if (cs.overflow === 'hidden' || cs.overflowX === 'hidden' || cs.overflowY === 'hidden') {
      htmlEl.style.setProperty('overflow', 'visible', 'important');
      htmlEl.style.setProperty('overflow-x', 'visible', 'important');
      htmlEl.style.setProperty('overflow-y', 'visible', 'important');
    }
  });

  // Template-01: в обычном UI nowrap/жёсткие флексы допустимы, но в PDF могут давать обрезку справа.
  clone.querySelectorAll('.template-template-01 .whitespace-nowrap').forEach((el) => {
    const h = el as HTMLElement;
    h.style.setProperty('white-space', 'normal', 'important');
    h.style.setProperty('overflow-wrap', 'anywhere', 'important');
    h.style.setProperty('word-break', 'break-word', 'important');
  });
  clone.querySelectorAll('.template-template-01 .section-content').forEach((el) => {
    const h = el as HTMLElement;
    h.style.setProperty('max-width', '100%', 'important');
    h.style.setProperty('overflow-wrap', 'anywhere', 'important');
    h.style.setProperty('word-break', 'break-word', 'important');
    h.style.setProperty('min-width', '0', 'important');
  });
  clone.querySelectorAll('.template-template-01 .flex').forEach((el) => {
    const h = el as HTMLElement;
    h.style.setProperty('min-width', '0', 'important');
  });

  const relaxNowrapForPdf = (root: HTMLElement) => {
    root.querySelectorAll('.template-template-01, .template-template-01 *').forEach((el) => {
      const h = el as HTMLElement;
      const ws = window.getComputedStyle(h).whiteSpace;
      if (ws === 'nowrap' || ws === 'pre') {
        h.style.setProperty('white-space', 'normal', 'important');
        h.style.setProperty('overflow-wrap', 'anywhere', 'important');
        h.style.setProperty('word-break', 'break-word', 'important');
      }
    });
  };

  // Сохраняем точный цвет фона как в превью: копируем computed background с оригинала на клон
  const origSheetContent = a4Sheet.querySelector('.a4-sheet-content') as HTMLElement | null;
  const origSingleContent = a4Sheet.querySelector('.single-content') as HTMLElement | null;
  if (origSheetContent) {
    const bg = window.getComputedStyle(origSheetContent).backgroundColor;
    if (bg && bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent') {
      const cloneSheetContent = clone.querySelector('.a4-sheet-content') as HTMLElement | null;
      if (cloneSheetContent) cloneSheetContent.style.setProperty('background', bg, 'important');
    }
  }
  if (origSingleContent) {
    const bg = window.getComputedStyle(origSingleContent).backgroundColor;
    if (bg && bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent') {
      const cloneSingleContent = clone.querySelector('.single-content') as HTMLElement | null;
      if (cloneSingleContent) cloneSingleContent.style.setProperty('background', bg, 'important');
    }
  }
  const origT03Sidebar = a4Sheet.querySelector('.t03-sidebar') as HTMLElement | null;
  const cloneT03Sidebar = clone.querySelector('.t03-sidebar') as HTMLElement | null;
  let forcedAccent: string | null = null;
  if (template && overrides) {
    const applied = applyOverrides(template, overrides);
    forcedAccent =
      applied.colors?.accent ||
      (template.id === 'template-01' ? applied.colors?.primary : applied.colors?.secondary) ||
      '#303030';
  }
  if (cloneT03Sidebar && forcedAccent) {
    const sidebarBg =
      forcedAccent.startsWith('#') ? forcedAccent + '66' : forcedAccent.startsWith('rgb')
        ? forcedAccent.replace(')', ', 0.4)').replace('rgb(', 'rgba(')
        : forcedAccent + '66';
    cloneT03Sidebar.style.setProperty('background', sidebarBg, 'important');
  } else if (origT03Sidebar) {
    const bg = window.getComputedStyle(origT03Sidebar).backgroundColor;
    if (bg && bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent') {
      if (cloneT03Sidebar) cloneT03Sidebar.style.setProperty('background', bg, 'important');
    }
  }
  if (forcedAccent) {
    clone.querySelectorAll('.t03-section-title-outline').forEach((el) => {
      (el as HTMLElement).style.setProperty('border', `1px solid ${forcedAccent}`, 'important');
    });
    clone.querySelectorAll('.t03-bar-seg').forEach((el) => {
      (el as HTMLElement).style.setProperty('background', forcedAccent!, 'important');
    });
    clone.querySelectorAll('.template-01-header').forEach((el) => {
      (el as HTMLElement).style.setProperty('background', forcedAccent!, 'important');
    });
  }
  const origSheetBg = window.getComputedStyle(a4Sheet).backgroundColor;
  if (origSheetBg && origSheetBg !== 'rgba(0, 0, 0, 0)' && origSheetBg !== 'transparent') {
    clone.style.setProperty('background', origSheetBg, 'important');
  }

  wrapper.appendChild(clone);
  document.body.appendChild(wrapper);

  try {
    await new Promise(resolve => setTimeout(resolve, 500));

    void clone.offsetHeight;

    const sheetContentRef = clone.querySelector('.a4-sheet-content') as HTMLElement | null;
    [clone, sheetContentRef].forEach((el) => {
      if (!el) return;
      const cs = window.getComputedStyle(el);
      if (cs.overflow === 'hidden' || cs.overflowY === 'hidden') {
        el.style.setProperty('overflow', 'visible', 'important');
      }
      if (cs.height && cs.height.includes('%')) {
        el.style.setProperty('height', 'auto', 'important');
      }
    });

    await new Promise(resolve => setTimeout(resolve, 200));
    void clone.offsetHeight;

    // После вставки в DOM снимаем nowrap даже с инлайновых стилей (иначе строки уезжают за 794px и html2canvas режет справа).
    relaxNowrapForPdf(clone);
    void clone.offsetHeight;

    const scrollH = clone.scrollHeight;
    const offsetH = clone.offsetHeight;

    let contentBottom = 0;
    let contentRight = 0;
    let contentLeft = Number.POSITIVE_INFINITY;
    const cloneRect = clone.getBoundingClientRect();
    clone.querySelectorAll('div, p, h1, h2, h3, h4, span, ul, li, table, tr, td').forEach((el) => {
      const rect = el.getBoundingClientRect();
      const bottom = rect.bottom - cloneRect.top;
      if (bottom > contentBottom) contentBottom = bottom;
      const left = rect.left - cloneRect.left;
      if (left < contentLeft) contentLeft = left;
      const right = rect.right - cloneRect.left;
      if (right > contentRight) contentRight = right;
    });

    const boundedLeft = Number.isFinite(contentLeft) ? contentLeft : 0;
    const contentSpanWidth = Math.max(0, Math.ceil(contentRight - boundedLeft));
    /** Реальная высота контента (без подгонки под лист). */
    const naturalContentPx = Math.max(
      1,
      scrollH,
      offsetH,
      Math.ceil(contentBottom) + 24
    );
    /**
     * Короткое резюме: контент по высоте ≤ A4 → снимок ровно A4, PDF-страница A4, снизу белое поле.
     * Длинное: высота PDF и снимка по контенту (не ужимаем в 297 мм).
     */
    const isShortResume = naturalContentPx <= A4_HEIGHT_PX;
    const captureHeightPx = isShortResume ? A4_HEIGHT_PX : naturalContentPx;
    const maxContentWidthPx = Math.max(
      A4_WIDTH_PX,
      clone.scrollWidth || 0,
      clone.offsetWidth || 0,
      Math.ceil(contentRight) || 0,
      contentSpanWidth
    );
    console.log('[PDF] mode=', isShortResume ? 'short(A4+whitespace)' : 'tall(content-height)',
      '| naturalH=', naturalContentPx, 'captureH=', captureHeightPx,
      '| A4px=', A4_HEIGHT_PX,
      '| maxContentW=', maxContentWidthPx);

    clone.style.setProperty('height', `${captureHeightPx}px`, 'important');
    wrapper.style.height = `${captureHeightPx}px`;

    const imgs = clone.querySelectorAll('img');
    await Promise.all(Array.from(imgs).map((img) => {
      const el = img as HTMLImageElement;
      if (el.complete && el.naturalWidth > 0) return Promise.resolve();
      return new Promise<void>((resolve) => {
        el.onload = () => resolve();
        el.onerror = () => resolve();
        setTimeout(resolve, 5000);
      });
    }));

    imgs.forEach((img) => {
      const el = img as HTMLImageElement;
      const cs = window.getComputedStyle(el);

      if (cs.objectFit === 'cover' && el.naturalWidth > 0 && el.naturalHeight > 0) {
        const cW = el.offsetWidth || parseInt(cs.width) || 80;
        const cH = el.offsetHeight || parseInt(cs.height) || 80;
        const imgRatio = el.naturalWidth / el.naturalHeight;
        const boxRatio = cW / cH;

        let rW: number, rH: number;
        if (imgRatio > boxRatio) {
          rH = cH; rW = Math.round(cH * imgRatio);
        } else {
          rW = cW; rH = Math.round(cW / imgRatio);
        }

        const parent = el.parentElement;
        if (parent) {
          parent.style.setProperty('overflow', 'hidden');
          parent.style.setProperty('position', 'relative');
          parent.style.setProperty('width', `${cW}px`);
          parent.style.setProperty('height', `${cH}px`);
        }

        el.removeAttribute('width');
        el.removeAttribute('height');
        el.style.cssText = `
          position: absolute; top: 50%; left: 50%;
          transform: translate(-50%, -50%);
          width: ${rW}px; height: ${rH}px;
          max-width: none; max-height: none;
          display: block;
        `;
        return;
      }

      const attrW = el.getAttribute('width');
      const attrH = el.getAttribute('height');
      let widthPx: number;
      let heightPx: number;

      if (attrW && attrH) {
        widthPx = parseInt(attrW, 10);
        heightPx = parseInt(attrH, 10);
      } else if (el.offsetWidth > 0 && el.offsetHeight > 0) {
        widthPx = el.offsetWidth;
        heightPx = el.offsetHeight;
      } else if (el.naturalWidth > 0 && el.naturalHeight > 0) {
        const MAX_DIM = 200;
        const ratio = Math.min(MAX_DIM / el.naturalWidth, MAX_DIM / el.naturalHeight, 1);
        widthPx = Math.round(el.naturalWidth * ratio);
        heightPx = Math.round(el.naturalHeight * ratio);
      } else {
        widthPx = 128;
        heightPx = 128;
      }

      el.setAttribute('width', String(widthPx));
      el.setAttribute('height', String(heightPx));
      el.style.setProperty('width', `${widthPx}px`, 'important');
      el.style.setProperty('height', `${heightPx}px`, 'important');
      el.style.setProperty('min-width', `${widthPx}px`, 'important');
      el.style.setProperty('max-width', `${widthPx}px`, 'important');
      el.style.setProperty('min-height', `${heightPx}px`, 'important');
      el.style.setProperty('max-height', `${heightPx}px`, 'important');
      el.style.setProperty('object-fit', 'cover', 'important');
      el.style.removeProperty('aspect-ratio');
    });

    clone.querySelectorAll('.t02-skill-label').forEach((label) => {
      const el = label as HTMLElement;
      const dotEl = el.querySelector('.t02-skill-dot') as HTMLElement | null;
      const bg = dotEl ? window.getComputedStyle(dotEl).backgroundColor : '#ccc';
      const textEl = el.querySelector('span:not(.t02-skill-dot)') as HTMLElement | null;
      const text = textEl ? textEl.textContent || '' : '';
      const fontSize = textEl ? window.getComputedStyle(textEl).fontSize : '12px';

      el.style.cssText = 'display: block; white-space: nowrap; padding: 0; margin: 0;';
      el.innerHTML = `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${bg};vertical-align:-1px;margin-right:6px;"></span><span style="display:inline;font-size:${fontSize};line-height:1;">${text}</span>`;
    });

    const overflowW = (clone.scrollWidth || 0) - A4_WIDTH_PX;
    if (overflowW > 2) {
      clone.style.setProperty('width', `${clone.scrollWidth}px`, 'important');
      clone.style.setProperty('max-width', 'none', 'important');
      wrapper.style.width = `${clone.scrollWidth}px`;
      void clone.offsetHeight;
      console.warn('[PDF] Контент шире A4 на', overflowW, 'px — расширяем снимок, в PDF всё влезет за счёт масштаба');
    }

    // Не передаём width/height в html2canvas — иначе легко получить обрезку по «окну», не совпадающему с раскладкой.
    const canvas = await html2canvas(clone, {
      scale: SCALE,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      logging: false,
      imageTimeout: 15000,
      removeContainer: false,
      foreignObjectRendering: false,
    });

    const imgData = canvas.toDataURL('image/png');
    const horizontalSafeMM = 1.5;
    const innerWMM = pageWidthMM - horizontalSafeMM * 2;
    const canvasAr = canvas.width / canvas.height;

    let pdf: jsPDF;
    if (isShortResume) {
      // Страница строго A4; картинка вписана целиком, снизу пусто на листе.
      const maxHMM = pageHeightMM;
      let drawWMM = innerWMM;
      let drawHMM = drawWMM / canvasAr;
      if (drawHMM > maxHMM) {
        drawHMM = maxHMM;
        drawWMM = drawHMM * canvasAr;
      }
      const xMM = horizontalSafeMM + (innerWMM - drawWMM) / 2;
      pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      pdf.addImage(imgData, 'PNG', xMM, 0, drawWMM, drawHMM);
    } else {
      // Высота страницы = высота контента (масштаб по ширине 210 мм), без ужатия в A4.
      let drawWMM = innerWMM;
      let drawHMM = drawWMM / canvasAr;
      const pageHmm = Math.max(drawHMM, 1);
      pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: [pageWidthMM, pageHmm]
      });
      pdf.addImage(imgData, 'PNG', horizontalSafeMM, 0, drawWMM, drawHMM);
    }

    return pdf;
  } finally {
    document.body.removeChild(wrapper);
  }
}

/**
 * Генерирует PDF из предпросмотра резюме через клонирование DOM-элемента и сохраняет файл.
 * Подход: клонируем .a4-sheet, размещаем off-screen, html2canvas, разбивка по страницам A4.
 */
export const generatePDFFromPreview = async (
  resumeData: ResumeData,
  template?: Template,
  overrides?: UserOverrides
): Promise<void> => {
  const pdf = await buildPDFFromPreview(resumeData, template, overrides);
  pdf.save(`resume-${resumeData.name?.first || 'resume'}-${resumeData.name?.last || ''}.pdf`);
};

/**
 * Генерирует PDF из предпросмотра резюме и возвращает Blob (для просмотра в новой вкладке).
 * template/overrides — для принудительного фона сайдбара (отклики).
 */
export const generatePDFFromPreviewAsBlob = async (
  resumeData: ResumeData,
  template?: Template,
  overrides?: UserOverrides
): Promise<Blob> => {
  const pdf = await buildPDFFromPreview(resumeData, template, overrides);
  return pdf.output('blob');
};

/**
 * Генерирует PDF из данных резюме (fallback метод).
 * Используется когда preview элемент недоступен (например, на странице проектов).
 */
export const generatePDF = async (resumeData: ResumeData, template: Template, overrides: UserOverrides = {}): Promise<void> => {
  try {
    const previewElement = document.querySelector('.a4-preview-container[data-pdf-export="true"] .a4-sheet');
    if (previewElement) {
      await generatePDFFromPreview(resumeData, template, overrides);
      return;
    }
  } catch (e) {
    console.warn('Fallback to HTML generation for PDF:', e);
  }

  try {
    const pageWidth = 210;
    const pageHeight = 297;
    
    const useMargins = template.id !== 'modern-sidebar-dark';
    const marginTop = useMargins ? 20 : 0;
    const marginBottom = useMargins ? 20 : 0;
    const marginLeft = useMargins ? 15 : 0;
    const marginRight = useMargins ? 15 : 0;
    const contentWidth = pageWidth - marginLeft - marginRight;
    const contentHeight = pageHeight - marginTop - marginBottom;
    
    const tempDiv = document.createElement('div');
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = '-9999px';
    tempDiv.style.top = '-9999px';
    tempDiv.style.width = `${contentWidth * 3.779527559}px`;
    tempDiv.style.height = `${contentHeight * 3.779527559}px`;
    tempDiv.style.background = 'white';
    tempDiv.className = 'resume-content';
    tempDiv.style.fontSize = '16px';
    tempDiv.style.lineHeight = '1.5';
    tempDiv.style.color = '#111';
    tempDiv.style.wordWrap = 'break-word';
    tempDiv.style.overflow = 'hidden';
    tempDiv.style.padding = '0';
    tempDiv.style.boxSizing = 'border-box';
    tempDiv.style.margin = '0';
    
    const styleElement = document.createElement('style');
    styleElement.textContent = getResumeStyles(template, overrides);
    document.head.appendChild(styleElement);
    
    const appliedTemplate = {
      ...template,
      colors: { ...template.colors, ...overrides.colors },
      fonts: {
        ...template.fonts,
        baseFamily: overrides.fontFamily ? [overrides.fontFamily, ...template.fonts.baseFamily] : template.fonts.baseFamily,
        scale: overrides.fontSize ? {
          ...template.fonts.scale,
          base: overrides.fontSize
        } : template.fonts.scale
      }
    };

    const resumeHTML = generateResumeHTML(resumeData, appliedTemplate);
    tempDiv.innerHTML = resumeHTML;
    
    document.body.appendChild(tempDiv);
    
    const canvas = await html2canvas(tempDiv, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      scrollX: 0,
      scrollY: 0,
      logging: false,
      imageTimeout: 15000,
      removeContainer: false,
      foreignObjectRendering: false
    });
    
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });
    
    const imgWidth = contentWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    
    pdf.addImage(imgData, 'PNG', marginLeft, marginTop, imgWidth, imgHeight);
    
    pdf.save(`resume-${resumeData.name?.first || 'resume'}-${resumeData.name?.last || ''}.pdf`);
    
    document.body.removeChild(tempDiv);
    document.head.removeChild(styleElement);
    
  } catch (error) {
    console.error('Ошибка генерации PDF:', error);
    throw error;
  }
};

/**
 * Генерирует PDF только из данных резюме (HTML + html2canvas), без использования DOM превью.
 * Используется для скачивания из откликов, чтобы результат совпадал с кнопкой «Скачать PDF» на странице резюме (fallback-путь).
 */
export const generatePDFFromData = async (
  resumeData: ResumeData,
  template: Template,
  overrides: UserOverrides = {}
): Promise<void> => {
  const pageWidth = 210;
  const pageHeight = 297;
  const useMargins = template.id !== 'modern-sidebar-dark';
  const marginTop = useMargins ? 20 : 0;
  const marginBottom = useMargins ? 20 : 0;
  const marginLeft = useMargins ? 15 : 0;
  const marginRight = useMargins ? 15 : 0;
  const contentWidth = pageWidth - marginLeft - marginRight;
  const contentHeight = pageHeight - marginTop - marginBottom;

  const tempDiv = document.createElement('div');
  tempDiv.style.position = 'absolute';
  tempDiv.style.left = '-9999px';
  tempDiv.style.top = '-9999px';
  tempDiv.style.width = `${contentWidth * 3.779527559}px`;
  tempDiv.style.height = `${contentHeight * 3.779527559}px`;
  tempDiv.style.background = 'white';
  tempDiv.className = 'resume-content';
  tempDiv.style.fontSize = '16px';
  tempDiv.style.lineHeight = '1.5';
  tempDiv.style.color = '#111';
  tempDiv.style.wordWrap = 'break-word';
  tempDiv.style.overflow = 'hidden';
  tempDiv.style.padding = '0';
  tempDiv.style.boxSizing = 'border-box';
  tempDiv.style.margin = '0';

  const styleElement = document.createElement('style');
  styleElement.textContent = getResumeStyles(template, overrides);
  document.head.appendChild(styleElement);

  const appliedTemplate = {
    ...template,
    colors: { ...template.colors, ...overrides.colors },
    fonts: {
      ...template.fonts,
      baseFamily: overrides.fontFamily ? [overrides.fontFamily, ...template.fonts.baseFamily] : template.fonts.baseFamily,
      scale: overrides.fontSize ? { ...template.fonts.scale, base: overrides.fontSize } : template.fonts.scale
    }
  };

  const resumeHTML = generateResumeHTML(resumeData, appliedTemplate);
  tempDiv.innerHTML = resumeHTML;
  document.body.appendChild(tempDiv);

  try {
    const canvas = await html2canvas(tempDiv, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      scrollX: 0,
      scrollY: 0,
      logging: false,
      imageTimeout: 15000,
      removeContainer: false,
      foreignObjectRendering: false
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    const imgWidth = contentWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    pdf.addImage(imgData, 'PNG', marginLeft, marginTop, imgWidth, imgHeight);

    pdf.save(`resume-${resumeData.name?.first || 'resume'}-${resumeData.name?.last || ''}.pdf`);
  } finally {
    document.body.removeChild(tempDiv);
    document.head.removeChild(styleElement);
  }
};

/**
 * Генерирует PDF из данных резюме и возвращает Blob (для просмотра в новой вкладке или кастомного скачивания).
 * Использует только fallback-метод (без DOM preview).
 */
export const generatePDFAsBlob = async (
  resumeData: ResumeData,
  template: Template,
  overrides: UserOverrides = {}
): Promise<Blob> => {
  const pageWidth = 210;
  const pageHeight = 297;
  const useMargins = template.id !== 'modern-sidebar-dark';
  const marginTop = useMargins ? 20 : 0;
  const marginBottom = useMargins ? 20 : 0;
  const marginLeft = useMargins ? 15 : 0;
  const marginRight = useMargins ? 15 : 0;
  const contentWidth = pageWidth - marginLeft - marginRight;
  const contentHeight = pageHeight - marginTop - marginBottom;

  const tempDiv = document.createElement('div');
  tempDiv.style.position = 'absolute';
  tempDiv.style.left = '-9999px';
  tempDiv.style.top = '-9999px';
  tempDiv.style.width = `${contentWidth * 3.779527559}px`;
  tempDiv.style.height = `${contentHeight * 3.779527559}px`;
  tempDiv.style.background = 'white';
  tempDiv.className = 'resume-content';
  tempDiv.style.fontSize = '16px';
  tempDiv.style.lineHeight = '1.5';
  tempDiv.style.color = '#111';
  tempDiv.style.wordWrap = 'break-word';
  tempDiv.style.overflow = 'hidden';
  tempDiv.style.padding = '0';
  tempDiv.style.boxSizing = 'border-box';
  tempDiv.style.margin = '0';

  const styleElement = document.createElement('style');
  styleElement.textContent = getResumeStyles(template, overrides);
  document.head.appendChild(styleElement);

  const appliedTemplate = {
    ...template,
    colors: { ...template.colors, ...overrides.colors },
    fonts: {
      ...template.fonts,
      baseFamily: overrides.fontFamily ? [overrides.fontFamily, ...template.fonts.baseFamily] : template.fonts.baseFamily,
      scale: overrides.fontSize ? { ...template.fonts.scale, base: overrides.fontSize } : template.fonts.scale
    }
  };

  const resumeHTML = generateResumeHTML(resumeData, appliedTemplate);
  tempDiv.innerHTML = resumeHTML;
  document.body.appendChild(tempDiv);

  try {
    const canvas = await html2canvas(tempDiv, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      scrollX: 0,
      scrollY: 0,
      logging: false,
      imageTimeout: 15000,
      removeContainer: false,
      foreignObjectRendering: false
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    const imgWidth = contentWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    pdf.addImage(imgData, 'PNG', marginLeft, marginTop, imgWidth, imgHeight);

    const blob = pdf.output('blob');
    return blob;
  } finally {
    document.body.removeChild(tempDiv);
    document.head.removeChild(styleElement);
  }
};
